// src/Admin/BlogManagement.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../Firebase/firebaseConfig';
import {
  collection,
  getDocs,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { uploadFile, deleteFile } from '../Firebase/storageBlog';
import ConfirmationModal from '../components/ConfirmationModal/ConfirmationModal';
import { ToastContainer, toast } from 'react-toastify';
import { Spinner, ProgressBar, Button, Form, Table, Image, Row, Col, InputGroup } from 'react-bootstrap';

const BlogManagement = () => {
  // State Variables
  const [blogs, setBlogs] = useState([]);
  const [blogForm, setBlogForm] = useState({
    title: { en: '', ar: '' },
    description: { en: '', ar: '' },
    content: { en: '', ar: '' },
    date: '',
    time: '',
    category: '',
    links: [], // Array of { name: { en: '', ar: '' }, url: '' }
    imageFile: null, // Primary Image
    imageUrl: '',
    primaryImageStoragePath: '',
    extraImages: [], // Array of File objects
    existingExtraImages: [], // Array of { url: '', storagePath: '' }
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [modalData, setModalData] = useState({
    isVisible: false,
    title: '',
    body: '',
    onConfirm: null,
  });

  const blogsCollection = collection(db, 'blogs');

  // Fetch Blogs from Firestore
  const fetchBlogs = useCallback(async () => {
    try {
      const q = query(blogsCollection, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const blogsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log('Fetched blogs:', blogsData);
      setBlogs(blogsData);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Failed to fetch blogs.');
    }
  }, [blogsCollection]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  // Handle Form Changes
  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name.includes('.')) {
      const [field, lang] = name.split('.');
      setBlogForm((prev) => ({
        ...prev,
        [field]: { ...prev[field], [lang]: value },
      }));
    } else if (name === 'imageFile') {
      const file = files[0];
      if (file) {
        const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
        const maxSize = 5 * 1024 * 1024; // 5MB

        if (!validTypes.includes(file.type)) {
          setFormErrors((prev) => ({ ...prev, imageFile: 'Invalid image type. Only JPEG, PNG, and GIF are allowed.' }));
          return;
        }

        if (file.size > maxSize) {
          setFormErrors((prev) => ({ ...prev, imageFile: 'Image size exceeds 5MB.' }));
          return;
        }

        setBlogForm((prev) => ({
          ...prev,
          imageFile: file,
        }));
        setFormErrors((prev) => ({ ...prev, imageFile: null }));
      }
    } else if (name === 'extraImages') {
      const selectedFiles = Array.from(files);
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      const invalidFiles = selectedFiles.filter(
        (file) => !validTypes.includes(file.type) || file.size > maxSize
      );

      if (invalidFiles.length > 0) {
        setFormErrors((prev) => ({ ...prev, extraImages: 'Some additional images are invalid. Only JPEG, PNG, and GIF images under 5MB are allowed.' }));
        return;
      }

      setBlogForm((prev) => ({
        ...prev,
        extraImages: selectedFiles,
      }));
      setFormErrors((prev) => ({ ...prev, extraImages: null }));
    } else {
      setBlogForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle Link Changes
  const handleLinkChange = (index, field, lang, value) => {
    const updatedLinks = blogForm.links.map((link, idx) => {
      if (idx === index) {
        if (field === 'name') {
          return {
            ...link,
            name: { ...link.name, [lang]: value },
          };
        } else {
          return {
            ...link,
            [field]: value,
          };
        }
      }
      return link;
    });
    setBlogForm((prev) => ({
      ...prev,
      links: updatedLinks,
    }));
  };

  // Add New Link
  const addNewLink = () => {
    setBlogForm((prev) => ({
      ...prev,
      links: [...prev.links, { name: { en: '', ar: '' }, url: '' }],
    }));
  };

  // Remove Link
  const removeLink = (index) => {
    const updatedLinks = blogForm.links.filter((_, idx) => idx !== index);
    setBlogForm((prev) => ({
      ...prev,
      links: updatedLinks,
    }));
  };

  // Validate Form
  const validateForm = () => {
    const errors = {};
    if (!blogForm.title.en.trim()) errors['title.en'] = 'English title is required.';
    if (!blogForm.title.ar.trim()) errors['title.ar'] = 'Arabic title is required.';
    if (!blogForm.category.trim()) errors.category = 'Category is required.';
    if (blogForm.category === 'upcoming-events' && !blogForm.time.trim()) {
      errors.time = 'Time is required for upcoming events.';
    }
    if (!isEditing && !blogForm.imageFile) errors.imageFile = 'Primary image is required.';

    // Validate extraImages if any
    if (blogForm.extraImages.length > 0) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      const invalidFiles = blogForm.extraImages.filter(
        (file) => !validTypes.includes(file.type) || file.size > maxSize
      );
      if (invalidFiles.length > 0) {
        errors.extraImages = 'Some additional images are invalid. Only JPEG, PNG, and GIF images under 5MB are allowed.';
      }
    }

    // Validate links
    blogForm.links.forEach((link, index) => {
      if (!link.name.en.trim()) {
        errors[`links.${index}.name.en`] = `English name for link ${index + 1} is required.`;
      }
      if (!link.name.ar.trim()) {
        errors[`links.${index}.name.ar`] = `Arabic name for link ${index + 1} is required.`;
      }
      if (!link.url.trim()) {
        errors[`links.${index}.url`] = `URL for link ${index + 1} is required.`;
      } else {
        // Simple URL validation
        const urlPattern = new RegExp('^(https?:\\/\\/)?'+ // protocol
          '((([a-zA-Z\\d]([a-zA-Z\\d-]*[a-zA-Z\\d])*)\\.)+[a-zA-Z]{2,})'+ // domain name
          '(\\:\\d+)?(\\/[-a-zA-Z\\d%_.~+]*)*'+ // port and path
          '(\\?[;&a-zA-Z\\d%_.~+=-]*)?'+ // query string
          '(\\#[-a-zA-Z\\d_]*)?$','i');
        if (!urlPattern.test(link.url)) {
          errors[`links.${index}.url`] = `Invalid URL format for link ${index + 1}.`;
        }
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Form Submission
  const handleSaveBlog = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.warn('Please fill all required fields correctly.');
      return;
    }

    setIsProcessing(true);
    setUploadProgress(0);

    try {
      let imageUrl = blogForm.imageUrl;
      let primaryImageStoragePath = blogForm.primaryImageStoragePath;
      let existingExtraImages = blogForm.existingExtraImages;

      // Handle Primary Image
      if (blogForm.imageFile) {
        // If editing and there's an existing primary image, delete it
        if (isEditing && primaryImageStoragePath) {
          try {
            await deleteFile(primaryImageStoragePath);
          } catch (error) {
            console.warn('Failed to delete existing primary image:', error);
            toast.warn('Failed to delete existing primary image.');
          }
        }

        // Upload new primary image
        const { storagePath, downloadURL } = await uploadFile(
          blogForm.imageFile,
          'blogs/primary',
          setUploadProgress
        );

        imageUrl = downloadURL;
        primaryImageStoragePath = storagePath;
      }

      // Handle Additional Images
      let uploadedExtraImages = [];
      if (blogForm.extraImages.length > 0) {
        const uploadPromises = blogForm.extraImages.map((file) =>
          uploadFile(file, 'blogs/extra', (progress) => {
            // Optional: Aggregate progress if needed
            // For simplicity, we'll update uploadProgress to the maximum of all
            setUploadProgress((prev) => Math.max(prev, progress));
          })
        );

        const uploadResults = await Promise.all(uploadPromises);
        uploadedExtraImages = uploadResults.map(({ storagePath, downloadURL }) => ({
          url: downloadURL,
          storagePath,
        }));
      }

      // Combine existing and new extra images
      const combinedExtraImages = [...existingExtraImages, ...uploadedExtraImages];

      const blogData = {
        title: blogForm.title,
        description: blogForm.description,
        content: blogForm.content,
        date: blogForm.date,
        time: blogForm.time,
        category: blogForm.category,
        links: blogForm.links,
        imageUrl: imageUrl || '',
        primaryImageStoragePath: primaryImageStoragePath || '',
        extraImages: combinedExtraImages,
        updatedAt: new Date().toISOString(),
      };

      if (!isEditing) {
        blogData.createdAt = new Date().toISOString();
      }

      if (isEditing && editingBlogId) {
        // Update existing blog
        await updateDoc(doc(db, 'blogs', editingBlogId), blogData);
        toast.success('Blog updated successfully.');
      } else {
        // Add new blog
        await addDoc(blogsCollection, blogData);
        toast.success('Blog added successfully.');
      }

      // Reset the form
      resetForm();
      fetchBlogs();
    } catch (error) {
      console.error('Error saving blog:', error);
      toast.error(`Failed to save blog: ${error.message}`);
    } finally {
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  // Reset Form
  const resetForm = () => {
    setBlogForm({
      title: { en: '', ar: '' },
      description: { en: '', ar: '' },
      content: { en: '', ar: '' },
      date: '',
      time: '',
      category: '',
      links: [],
      imageFile: null,
      imageUrl: '',
      primaryImageStoragePath: '',
      extraImages: [],
      existingExtraImages: [],
    });
    setIsEditing(false);
    setEditingBlogId(null);
    setFormErrors({});
    setUploadProgress(0);
  };

  // Handle Editing a Blog
  const handleEditBlog = (blog) => {
    setBlogForm({
      title: blog.title || { en: '', ar: '' },
      description: blog.description || { en: '', ar: '' },
      content: blog.content || { en: '', ar: '' },
      date: blog.date || '',
      time: blog.time || '',
      category: blog.category || '',
      links: blog.links || [],
      imageFile: null, // New image to be uploaded
      imageUrl: blog.imageUrl || '',
      primaryImageStoragePath: blog.primaryImageStoragePath || '',
      extraImages: [],
      existingExtraImages: blog.extraImages || [],
    });
    setIsEditing(true);
    setEditingBlogId(blog.id);
    window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top for better UX
  };

  // Handle Deleting a Blog
  const handleDeleteBlog = (blog) => {
    setModalData({
      isVisible: true,
      title: 'Delete Blog',
      body: `Are you sure you want to delete the blog "${blog.title.en}"?`,
      onConfirm: () => confirmDeleteBlog(blog.id, blog.primaryImageStoragePath, blog.extraImages),
    });
  };

  // Confirm Deletion
  const confirmDeleteBlog = async (blogId, primaryStoragePath, extraImages) => {
    setIsProcessing(true);
    try {
      // Delete primary image
      if (primaryStoragePath) {
        try {
          await deleteFile(primaryStoragePath);
        } catch (error) {
          console.warn('Failed to delete primary image:', error);
          toast.warn('Failed to delete primary image.');
        }
      }

      // Delete extra images
      if (extraImages && extraImages.length > 0) {
        const deletePromises = extraImages.map((img) => deleteFile(img.storagePath));
        try {
          await Promise.all(deletePromises);
        } catch (error) {
          console.warn('Failed to delete some extra images:', error);
          toast.warn('Failed to delete some extra images.');
        }
      }

      // Delete blog document
      await deleteDoc(doc(db, 'blogs', blogId));
      toast.success('Blog deleted successfully.');
      fetchBlogs();
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast.error(`Failed to delete blog: ${error.message}`);
    } finally {
      setIsProcessing(false);
      setModalData({
        isVisible: false,
        title: '',
        body: '',
        onConfirm: null,
      });
    }
  };

  // Handle Modal Cancellation
  const handleCancelModal = () => {
    setModalData({
      isVisible: false,
      title: '',
      body: '',
      onConfirm: null,
    });
  };

  return (
    <section id="blogs-section" className="admin-section container my-5">
      {/* Toast Notifications */}
      <ToastContainer />

      <h2 className="mb-4">Manage Blog Posts</h2>
      <Form onSubmit={handleSaveBlog}>
        {/* Title English */}
        <Form.Group className="mb-3" controlId="titleEn">
          <Form.Label>Title (English) <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            name="title.en"
            placeholder="Enter English Title"
            value={blogForm.title.en}
            onChange={handleInputChange}
            isInvalid={!!formErrors['title.en']}
            required
          />
          <Form.Control.Feedback type="invalid">
            {formErrors['title.en']}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Title Arabic */}
        <Form.Group className="mb-3" controlId="titleAr">
          <Form.Label>Title (Arabic) <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            name="title.ar"
            placeholder="Enter Arabic Title"
            value={blogForm.title.ar}
            onChange={handleInputChange}
            isInvalid={!!formErrors['title.ar']}
            required
          />
          <Form.Control.Feedback type="invalid">
            {formErrors['title.ar']}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Description English */}
        <Form.Group className="mb-3" controlId="descriptionEn">
          <Form.Label>Description (English)</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="description.en"
            placeholder="Enter English Description"
            value={blogForm.description.en}
            onChange={handleInputChange}
            isInvalid={!!formErrors['description.en']}
          />
          <Form.Control.Feedback type="invalid">
            {formErrors['description.en']}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Description Arabic */}
        <Form.Group className="mb-3" controlId="descriptionAr">
          <Form.Label>Description (Arabic)</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="description.ar"
            placeholder="Enter Arabic Description"
            value={blogForm.description.ar}
            onChange={handleInputChange}
            isInvalid={!!formErrors['description.ar']}
          />
          <Form.Control.Feedback type="invalid">
            {formErrors['description.ar']}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Content English */}
        <Form.Group className="mb-3" controlId="contentEn">
          <Form.Label>Content (English)</Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            name="content.en"
            placeholder="Enter English Content"
            value={blogForm.content.en}
            onChange={handleInputChange}
            isInvalid={!!formErrors['content.en']}
          />
          <Form.Control.Feedback type="invalid">
            {formErrors['content.en']}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Content Arabic */}
        <Form.Group className="mb-3" controlId="contentAr">
          <Form.Label>Content (Arabic)</Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            name="content.ar"
            placeholder="Enter Arabic Content"
            value={blogForm.content.ar}
            onChange={handleInputChange}
            isInvalid={!!formErrors['content.ar']}
          />
          <Form.Control.Feedback type="invalid">
            {formErrors['content.ar']}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Category */}
        <Form.Group className="mb-3" controlId="category">
          <Form.Label>Category <span className="text-danger">*</span></Form.Label>
          <Form.Select
            name="category"
            value={blogForm.category}
            onChange={handleInputChange}
            isInvalid={!!formErrors.category}
            required
          >
            <option value="" disabled>Select Category</option>
            <option value="upcoming-events">Upcoming Events</option>
            <option value="last-news">Last News</option>
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {formErrors.category}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Date */}
        <Form.Group className="mb-3" controlId="date">
          <Form.Label>Date</Form.Label>
          <Form.Control
            type="date"
            name="date"
            value={blogForm.date}
            onChange={handleInputChange}
            isInvalid={!!formErrors.date}
          />
          <Form.Control.Feedback type="invalid">
            {formErrors.date}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Time (only for upcoming events) */}
        {blogForm.category === 'upcoming-events' && (
          <Form.Group className="mb-3" controlId="time">
            <Form.Label>Time</Form.Label>
            <Form.Control
              type="time"
              name="time"
              value={blogForm.time}
              onChange={handleInputChange}
              isInvalid={!!formErrors.time}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.time}
            </Form.Control.Feedback>
          </Form.Group>
        )}

        {/* Related Links */}
        <Form.Group className="mb-3">
          <Form.Label>Related Links</Form.Label>
          {blogForm.links.map((link, index) => (
            <div key={index} className="mb-3 border p-3">
              <Row>
                <Col md={4}>
                  <Form.Group controlId={`linkNameEn-${index}`}>
                    <Form.Label>Name (English) <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={link.name.en}
                      onChange={(e) => handleLinkChange(index, 'name', 'en', e.target.value)}
                      isInvalid={!!formErrors[`links.${index}.name.en`]}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors[`links.${index}.name.en`]}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId={`linkNameAr-${index}`}>
                    <Form.Label>Name (Arabic) <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={link.name.ar}
                      onChange={(e) => handleLinkChange(index, 'name', 'ar', e.target.value)}
                      isInvalid={!!formErrors[`links.${index}.name.ar`]}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors[`links.${index}.name.ar`]}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId={`linkUrl-${index}`}>
                    <Form.Label>URL <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="url"
                      value={link.url}
                      onChange={(e) => handleLinkChange(index, 'url', null, e.target.value)}
                      isInvalid={!!formErrors[`links.${index}.url`]}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors[`links.${index}.url`]}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Button variant="danger" size="sm" onClick={() => removeLink(index)} className="mt-2">
                Remove Link
              </Button>
            </div>
          ))}
          <Button variant="primary" onClick={addNewLink}>
            Add New Link
          </Button>
        </Form.Group>

        {/* Primary Image */}
        <Form.Group className="mb-3" controlId="primaryImage">
          <Form.Label>
            Primary Image {isEditing ? '(Optional)' : <span className="text-danger">*</span>}
          </Form.Label>
          <Form.Control
            type="file"
            name="imageFile"
            accept="image/*"
            onChange={handleInputChange}
            isInvalid={!!formErrors.imageFile}
            required={!isEditing}
          />
          <Form.Control.Feedback type="invalid">
            {formErrors.imageFile}
          </Form.Control.Feedback>
          {/* Show existing image if editing and no new image is selected */}
          {isEditing && blogForm.imageUrl && !blogForm.imageFile && (
            <div className="mt-2">
              <Image src={blogForm.imageUrl} alt="Primary" thumbnail width={150} />
            </div>
          )}
          {/* Show image preview if a new image is selected */}
          {blogForm.imageFile && (
            <div className="mt-2">
              <Image src={URL.createObjectURL(blogForm.imageFile)} alt="Primary Preview" thumbnail width={150} />
            </div>
          )}
        </Form.Group>

        {/* Additional Images */}
        <Form.Group className="mb-3" controlId="additionalImages">
          <Form.Label>Additional Images</Form.Label>
          <Form.Control
            type="file"
            name="extraImages"
            accept="image/*"
            multiple
            onChange={handleInputChange}
            isInvalid={!!formErrors.extraImages}
          />
          <Form.Control.Feedback type="invalid">
            {formErrors.extraImages}
          </Form.Control.Feedback>
          {/* Show existing extra images if editing */}
          {isEditing && blogForm.existingExtraImages.length > 0 && (
            <Row className="mt-2">
              {blogForm.existingExtraImages.map((img, index) => (
                <Col key={index} xs={6} md={3} lg={2} className="mb-3">
                  <Image src={img.url} alt={`Extra ${index + 1}`} thumbnail />
                </Col>
              ))}
            </Row>
          )}
          {/* Show previews of new additional images */}
          {blogForm.extraImages.length > 0 && (
            <Row className="mt-2">
              {blogForm.extraImages.map((file, index) => (
                <Col key={index} xs={6} md={3} lg={2} className="mb-3">
                  <Image src={URL.createObjectURL(file)} alt={`Extra Preview ${index + 1}`} thumbnail />
                </Col>
              ))}
            </Row>
          )}
        </Form.Group>

        {/* Upload Progress Bar */}
        {uploadProgress > 0 && uploadProgress < 100 && (
          <Form.Group className="mb-3">
            <ProgressBar now={uploadProgress} label={`${Math.round(uploadProgress)}%`} />
          </Form.Group>
        )}

        {/* Submit and Reset Buttons */}
        <div className="d-flex gap-3">
          <Button variant="primary" type="submit" disabled={isProcessing}>
            {isProcessing ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{' '}
                Processing...
              </>
            ) : (
              isEditing ? 'Update Blog' : 'Add Blog'
            )}
          </Button>
          <Button variant="secondary" type="button" onClick={resetForm} disabled={isProcessing}>
            Reset Form
          </Button>
        </div>
      </Form>

      {/* Blogs Table */}
      <h2 className="mt-5 mb-4">Blog Posts</h2>
      <div className="table-responsive">
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th>Title (EN)</th>
              <th>Title (AR)</th>
              <th>Category</th>
              <th>Date</th>
              <th>Time</th>
              <th>Primary Image</th>
              <th>Extra Images</th>
              <th>Links</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {blogs.length > 0 ? (
              blogs.map((blog) => (
                <tr key={blog.id}>
                  <td>{blog.title?.en || 'N/A'}</td>
                  <td>{blog.title?.ar || 'N/A'}</td>
                  <td>{capitalizeFirstLetter(blog.category.replace('-', ' '))}</td>
                  <td>{blog.date ? new Date(blog.date).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    {blog.category === 'upcoming-events' ? (blog.time || 'N/A') : 'N/A'}
                  </td>
                  <td>
                    {blog.imageUrl ? (
                      <Image src={blog.imageUrl} alt="Primary" thumbnail width={100} />
                    ) : (
                      'No Image'
                    )}
                  </td>
                  <td>
                    {blog.extraImages && blog.extraImages.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {blog.extraImages.map((img, idx) => (
                          <Image key={idx} src={img.url} alt={`Extra ${idx + 1}`} thumbnail width={80} />
                        ))}
                      </div>
                    ) : (
                      'No Extra Images'
                    )}
                  </td>
                  <td>
                    {blog.links && blog.links.length > 0 ? (
                      <ul className="list-unstyled">
                        {blog.links.map((link, idx) => (
                          <li key={idx}>
                            <strong>EN:</strong> {link.name.en} <br />
                            <strong>AR:</strong> {link.name.ar} <br />
                            <a href={link.url} target="_blank" rel="noopener noreferrer">{link.url}</a>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      'No Links'
                    )}
                  </td>
                  <td>
                    <Button
                      variant="warning"
                      size="sm"
                      className="me-2"
                      onClick={() => handleEditBlog(blog)}
                      disabled={isProcessing}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteBlog(blog)}
                      disabled={isProcessing}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center">
                  No blog posts available.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Confirmation Modal */}
      {modalData.isVisible && (
        <ConfirmationModal
          title={modalData.title}
          body={modalData.body}
          onConfirm={modalData.onConfirm}
          onCancel={handleCancelModal}
          isProcessing={isProcessing}
        />
      )}
    </section>
  );
};

// Utility Function to Capitalize First Letter
const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export default BlogManagement;
